// BiometricLoginToggle — account setting to enroll / manage passkeys (biometric
// sign-in) for the signed-in user. Renders inside the Account modal opened from
// the navbar. Only meaningful when authenticated (Cognito rejects enrollment
// otherwise) and when the browser exposes the WebAuthn platform authenticator.
import React, { useState } from 'react';
import { usePasskeys } from '../../hooks/usePasskeys';
import FingerprintIcon from './FingerprintIcon';
import './BiometricLoginToggle.css';

function formatCreated(cred) {
  const raw = cred.createdAt || cred.CreatedAt;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function credLabel(cred) {
  const name = cred.friendlyCredentialName || cred.friendlyName;
  if (name) return name;
  const id = cred.credentialId || '';
  return id ? `Passkey ${id.slice(0, 8)}…` : 'Passkey';
}

// Turn Amplify/WebAuthn rejections into something a person can act on.
function friendlyError(err) {
  const name = err?.name || '';
  const msg = err?.message || '';
  if (name === 'NotAllowedError' || /cancel|timed out|not allowed/i.test(msg)) {
    return 'Biometric prompt was cancelled or timed out. You can try again.';
  }
  if (name === 'InvalidStateError' || /already registered|exists/i.test(msg)) {
    return 'This device already has a passkey on your account.';
  }
  if (/not authenticated|no current user|unauthenticated/i.test(msg)) {
    return 'Please sign in again before adding a passkey.';
  }
  return msg || 'Something went wrong. Please try again.';
}

const BiometricLoginToggle = () => {
  const { supported, credentials, enroll, remove } = usePasskeys();
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleEnroll = async () => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await enroll();
      setNotice('Biometric sign-in is enabled on this device.');
    } catch (err) {
      console.warn('Passkey enrollment failed:', err);
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (credentialId) => {
    // Guardrail: removal is easy to fumble, and losing your last passkey silently
    // would be surprising. Confirm, and remind that password + TOTP still works.
    const ok = window.confirm(
      'Remove this passkey? You can still sign in with your password and authenticator code.'
    );
    if (!ok) return;
    setError('');
    setNotice('');
    setRemovingId(credentialId);
    try {
      await remove(credentialId);
      setNotice('Passkey removed.');
    } catch (err) {
      console.warn('Passkey removal failed:', err);
      setError(friendlyError(err));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="biometric-toggle">
      <div className="biometric-toggle__head">
        <span className="biometric-toggle__icon" aria-hidden="true">
          <FingerprintIcon />
        </span>
        <div>
          <h3 className="biometric-toggle__title">Biometric sign-in</h3>
          <p className="biometric-toggle__subtitle">
            Use Face ID, Touch ID, Windows Hello, or your fingerprint to sign in on this device.
          </p>
        </div>
      </div>

      {!supported ? (
        <p className="biometric-toggle__unsupported">
          This browser or device doesn’t support biometric sign-in. You can still sign in with your
          password and authenticator code.
        </p>
      ) : (
        <>
          <button
            type="button"
            className="biometric-toggle__enroll"
            onClick={handleEnroll}
            disabled={busy}
          >
            {busy ? 'Waiting for biometric…' : 'Enable biometric sign-in on this device'}
          </button>

          {credentials.length > 0 && (
            <ul className="biometric-toggle__list">
              {credentials.map((cred) => {
                const created = formatCreated(cred);
                return (
                  <li key={cred.credentialId} className="biometric-toggle__item">
                    <div className="biometric-toggle__item-meta">
                      <span className="biometric-toggle__item-name">{credLabel(cred)}</span>
                      {created && (
                        <span className="biometric-toggle__item-date">Added {created}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="biometric-toggle__remove"
                      onClick={() => handleRemove(cred.credentialId)}
                      disabled={removingId === cred.credentialId}
                    >
                      {removingId === cred.credentialId ? 'Removing…' : 'Remove'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {error && <p className="biometric-toggle__error">{error}</p>}
      {notice && <p className="biometric-toggle__notice">{notice}</p>}

      <p className="biometric-toggle__recovery">
        Lost your device? You can always sign in with your password and authenticator code —
        passkeys are an extra, faster option, never the only way in.
      </p>
    </div>
  );
};

export default BiometricLoginToggle;
